import { z } from 'zod';
import { printAlignmentSchema, signatureBlockKeySchema } from './print-options';

/**
 * The signatories printed at the foot of every page.
 *
 * Who created the report (entered the results), who approved it, and the
 * reporting doctor — all read from the reports on the sheet, never typed into
 * the template. A sheet carrying several tests prints each person once; where
 * the tests were signed by different people, their names are listed together
 * and no single scan is drawn.
 *
 * `show_when_empty` is off by default, so a block appears only once somebody
 * is recorded against it: work in progress prints without an empty box.
 */

const signatureBlockSchema = z.object({
  key: signatureBlockKeySchema,
  /** "Report created by", "Approved by", "Reported by". */
  label: z.string().trim().max(60).default(''),
  enabled: z.boolean().default(true),
  show_image: z.boolean().default(true),
  show_qualifications: z.boolean().default(true),
  /** Consultant blocks only; ignored on the two staff blocks. */
  show_registration: z.boolean().default(true),
  /**
   * The completion timestamp under the verifier.
   *
   * Off by default: most labs date the header, and repeating it under a
   * signature reads as a second, possibly different, date.
   */
  show_timestamp: z.boolean().default(false),
  align: printAlignmentSchema.default('left'),
});
export type SignatureBlock = z.infer<typeof signatureBlockSchema>;

export const printSignaturesSchema = z.object({
  enabled: z.boolean().default(true),
  /**
   * Left to right.
   *
   * `.prefault`, not `.default`: the literals below are *input*, so each one
   * gets run through `signatureBlockSchema` and picks up every other field's
   * default. `.default` takes the parsed output and would demand all eight
   * fields spelled out three times.
   */
  blocks: z
    .array(signatureBlockSchema)
    .max(3)
    .prefault([
      { key: 'performed_by', label: 'Report created by' },
      { key: 'verified_by', label: 'Approved by' },
      { key: 'consultant', label: 'Reported by' },
    ]),
  /**
   * The box a signature image is fitted into, in millimetres.
   *
   * `fit`, not `width`/`height`: a scan is whatever aspect ratio the scanner
   * produced, and stretching one to a fixed box is how a signature ends up
   * looking forged.
   */
  image_height: z.coerce.number().min(0).max(40).default(12),
  image_width: z.coerce.number().min(0).max(120).default(40),
  /** A ruled line under each block, so a block with no image can be signed by hand. */
  rule: z.boolean().default(true),
  /**
   * Print a block for which nothing is recorded.
   *
   * False by default, which is what lets `blocks` default to all three: a
   * block appears only once the sheet has somebody to put in it.
   */
  show_when_empty: z.boolean().default(false),
  size: z.coerce.number().min(4).max(72).default(8),
});
export type PrintSignatures = z.infer<typeof printSignaturesSchema>;
